import { api } from '@/app/api'
import type { AuthUser, Company, ResourceResponse } from '@/lib/types'

interface LoginRequest {
  email: string
  password: string
}

/** POST /v1/auth/login -> AuthResource. No refresh token in this backend. */
interface LoginPayload {
  access_token: string
  token_type: string
  expires_in: number
  user: {
    id: string
    full_name: string
    email: string
    user_type: string
  }
}

/** GET /v1/auth/me -> MeResource (flat permissions + enabled_modules). */
interface MePayload {
  user: AuthUser
  company: Company | null
}

/** POST /v1/auth/register -> AuthResource (token issued on signup). */
export interface RegisterRequest {
  company_name: string
  subdomain: string
  first_name: string
  last_name: string
  email: string
  phone?: string
  password: string
  password_confirmation: string
  country_code?: string
  timezone?: string
  currency_code?: string
  industry_template_code?: string
  plan_code?: string
}

export interface PublicPlan {
  code: string
  name: string
  tier: 'starter' | 'growth' | 'scale' | 'enterprise'
  description: string | null
  monthly_price: number
  annual_price: number
  currency_code: string
  max_users: number | null
  max_customers: number | null
  max_products: number | null
  max_storage_gb: number | null
  features: string[] | null
}

export interface PublicIndustry {
  code: string
  name: string
  category: string
  description: string | null
  icon: string | null
}

export const authApi = api.injectEndpoints({
  endpoints: (build) => ({
    login: build.mutation<LoginPayload, LoginRequest>({
      query: (body) => ({ url: '/v1/auth/login', method: 'POST', body }),
      transformResponse: (response: ResourceResponse<LoginPayload>) => response.data,
      invalidatesTags: ['Auth'],
    }),

    register: build.mutation<LoginPayload, RegisterRequest>({
      query: (body) => ({ url: '/v1/auth/register', method: 'POST', body }),
      transformResponse: (response: ResourceResponse<LoginPayload>) => response.data,
      invalidatesTags: ['Auth'],
    }),

    me: build.query<MePayload, void>({
      query: () => '/v1/auth/me',
      transformResponse: (response: ResourceResponse<MePayload>) => response.data,
      providesTags: ['Auth'],
    }),

    logout: build.mutation<void, void>({
      query: () => ({ url: '/v1/auth/logout', method: 'POST' }),
      invalidatesTags: ['Auth'],
    }),

    // Public catalog (no auth) — for the signup wizard
    publicPlans: build.query<{ data: PublicPlan[] }, void>({
      query: () => '/v1/public/plans',
    }),

    publicIndustries: build.query<{ data: PublicIndustry[] }, void>({
      query: () => '/v1/public/industries',
    }),
  }),
  overrideExisting: false,
})

export const {
  useLoginMutation,
  useRegisterMutation,
  useMeQuery,
  useLazyMeQuery,
  useLogoutMutation,
  usePublicPlansQuery,
  usePublicIndustriesQuery,
} = authApi
